// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useRef, useEffect } from 'react';
import { Icon } from 'semantic-ui-react';
import { useAuth } from '../../context/AuthContext';

export default function UserMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout } = useAuth();
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Get initials from user name
  const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const handleLogout = async () => {
    setIsOpen(false);
    await logout();
  };

  return (
    <div ref={menuRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '0.5rem 0.75rem',
          borderRadius: '0.5rem',
          cursor: 'pointer',
          transition: 'background-color 0.2s',
          backgroundColor: isOpen ? '#f5f5f5' : 'transparent',
          border: 'none',
          background: isOpen ? '#f5f5f5' : 'transparent',
        }}
        onMouseEnter={(e) => {
          if (!isOpen) {
            e.currentTarget.style.backgroundColor = '#f9f9f9';
          }
        }}
        onMouseLeave={(e) => {
          if (!isOpen) {
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <div
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            backgroundColor: '#2185D0',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.875rem',
            fontWeight: 600,
          }}
          aria-hidden="true"
        >
          {user?.name ? getInitials(user.name) : 'AD'}
        </div>
        <div style={{ textAlign: 'left', flex: 1 }}>
          <div
            style={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: '#1b1c1d',
              lineHeight: 1.2,
            }}
          >
            {user?.name || 'Admin User'}
          </div>
          <div
            style={{
              fontSize: '0.75rem',
              color: '#767676',
              lineHeight: 1.2,
            }}
          >
            Platform Admin
          </div>
        </div>
        <Icon
          name={isOpen ? 'chevron up' : 'chevron down'}
          style={{
            color: '#767676',
            margin: 0,
            fontSize: '0.875rem',
          }}
        />
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 0.5rem)',
            backgroundColor: 'white',
            border: '1px solid rgba(34, 36, 38, 0.15)',
            borderRadius: '0.5rem',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            minWidth: '200px',
            zIndex: 1000,
            overflow: 'hidden',
          }}
          role="menu"
        >
          <div
            onClick={handleLogout}
            style={{
              padding: '0.75rem 1rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              transition: 'background-color 0.2s',
              color: '#1b1c1d',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f5f5f5';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            role="menuitem"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleLogout();
              }
            }}
          >
            <Icon name="sign out" style={{ margin: 0, color: '#db2828' }} />
            <span style={{ fontSize: '0.875rem' }}>Sign Out</span>
          </div>
        </div>
      )}
    </div>
  );
}
